import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import { useFormattedDate } from 'volto-ministerio-publico-federal/hooks/useFormattedDate';
import type { UnidadeResponsavelProps } from './types';

const TITULO = 'Área responsável';

/**
 * Canal de atendimento usado quando a unidade não publica contato nenhum.
 *
 * Caminho interno de propósito: o destino final ainda vai ser definido, e um
 * redirecionamento no portal é mais fácil de corrigir depois do que uma URL
 * absoluta espalhada pelo código.
 */
const OUVIDORIA_HREF = '/ouvidoria';

/**
 * Número discável a partir do que o admin digitou no painel.
 *
 * Mantém apenas dígitos e um eventual `+` inicial: "(61) 3030-9235" vira
 * "6130309235", e "+55 61 3030-9235" preserva o formato internacional. Não
 * inventamos DDI — quem cadastra decide se o número é local ou internacional.
 */
const toTelHref = (telefone: string): string =>
  `tel:${telefone.trim().replace(/(?!^\+)[^\d]/g, '')}`;

/**
 * Card que identifica a unidade gestora do conteúdo (RF03 — Lei de Acesso à
 * Informação), com contato e data da última atualização.
 *
 * A unidade já chega resolvida pelo backend em `unidade_responsavel_info`:
 * quando a página não tem valor próprio, o serializer devolve a unidade
 * herdada do ancestral mais próximo (`herdado: true`). Aqui só renderizamos —
 * não há busca na árvore no cliente.
 *
 * Chave separada do campo `unidade_responsavel`, que é o UID editado no
 * formulário. Telefone e e-mail chegam `null` quando a unidade os tem
 * cadastrados mas marcados para não exibir — a omissão é decidida no
 * backend, então aqui é o mesmo caminho de "não preenchido".
 *
 * Duas variantes do mesmo dado, escolhidas por quem monta a página (ver
 * `ContentContainer`): `sidebar` quando existe coluna lateral, `full` quando
 * o card vai no fim da página em largura total.
 */
export const UnidadeResponsavel: React.FC<UnidadeResponsavelProps> = ({
  content,
  variant = 'full',
  as: Heading = 'h2',
  disposicao = 'padrao',
  titulo = TITULO,
}) => {
  const { formatLongWithTime } = useFormattedDate();
  const unidade = content?.unidade_responsavel_info;

  // Sem unidade em lugar nenhum da árvore: o card não existe (decisão de
  // produto — nada de placeholder vazio na página pública).
  if (!unidade?.titulo) {
    return null;
  }

  const modified = content?.modified;
  const base = 'mpf-unidade-responsavel';

  const gestor = (
    <p className={`${base}__item`}>
      <span className={`${base}__label`}>Gestor responsável:</span>{' '}
      <span className={`${base}__value`}>{unidade.titulo}</span>
    </p>
  );

  // Telefone e e-mail são acionáveis: no celular discam e abrem o cliente de
  // e-mail. Ficam como link também no desktop — detectar dispositivo por user
  // agent quebraria a hidratação do SSR, e um `<a>` desativado por CSS seria
  // inacessível por teclado. Visualmente idênticos ao texto ao redor, com
  // sublinhado só no hover/foco (ver `_unidade-responsavel.scss`).
  const telefone = unidade.telefone ? (
    <p className={`${base}__item`}>
      <span className={`${base}__label`}>Telefone:</span>{' '}
      <a
        className={`${base}__value ${base}__link`}
        href={toTelHref(unidade.telefone)}
      >
        {unidade.telefone}
      </a>
    </p>
  ) : null;

  const email = unidade.email ? (
    <p className={`${base}__item`}>
      <span className={`${base}__label`}>E-mail:</span>{' '}
      <a
        className={`${base}__value ${base}__link`}
        href={`mailto:${unidade.email}`}
      >
        {unidade.email}
      </a>
    </p>
  ) : null;

  // Unidade sem nenhum contato público não pode virar um beco sem saída: a
  // página identifica quem responde por ela, mas não diz como falar com essa
  // pessoa. Como e-mail e telefone são obrigatórios no cadastro, chegar aqui
  // significa que os dois foram marcados como internos — decisão de quem
  // cadastrou, e o cidadão é encaminhado ao canal institucional.
  //
  // Só quando faltam OS DOIS: havendo e-mail publicado, já existe canal, e
  // convidar para a Ouvidoria ao lado de um telefone que funciona é ruído.
  const ouvidoria =
    !telefone && !email ? (
      <p className={`${base}__item ${base}__fallback`}>
        Para falar com este setor, entre em contato com a{' '}
        <UniversalLink href={OUVIDORIA_HREF} className={`${base}__link`}>
          Ouvidoria
        </UniversalLink>
        .
      </p>
    ) : null;

  const atualizacao = modified ? (
    <p className={`${base}__updated`}>
      {`Última atualização em ${formatLongWithTime(modified)}`}
    </p>
  ) : null;

  return (
    <section
      className={`${base} ${base}--${variant}${
        disposicao === 'linha' ? ` ${base}--linha` : ''
      }`}
    >
      <header className={`${base}__header`}>
        <Heading className={`${base}__title`}>{titulo}</Heading>
      </header>

      <div className={`${base}__body`}>
        {disposicao === 'linha' && variant === 'full' ? (
          <>
            <div className={`${base}__row`}>
              {[gestor, telefone, email, ouvidoria]
                .filter(Boolean)
                .map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <span
                        className={`${base}__separador`}
                        aria-hidden="true"
                      />
                    )}
                    {item}
                  </React.Fragment>
                ))}
            </div>
            {atualizacao}
          </>
        ) : variant === 'sidebar' ? (
          <>
            {gestor}
            {atualizacao}
            {/* Sempre há algo abaixo do divisor: os contatos publicados ou,
                na falta dos dois, o encaminhamento à Ouvidoria. */}
            <hr className={`${base}__divider`} />
            {telefone}
            {email}
            {ouvidoria}
          </>
        ) : (
          <>
            {/* Gestor e telefone dividem a primeira linha; o e-mail vai para
                a segunda. Os três numa linha só espremiam o texto quando o
                card acompanha a largura do conteúdo, quebrando no meio da
                palavra ("Seção de gestão de conteúdo / web"). */}
            <div className={`${base}__row`}>
              {gestor}
              {telefone}
            </div>
            {email && <div className={`${base}__row`}>{email}</div>}
            {ouvidoria && <div className={`${base}__row`}>{ouvidoria}</div>}
            {atualizacao}
          </>
        )}
      </div>
    </section>
  );
};

export default UnidadeResponsavel;
